import axios from "axios";
import {
  GET_COMPLIANCE_DROPDOWNS_URL,
  GET_CSRF_TOKEN,
  GET_COMPLIANCE_FIELDS_URL,
  GET_RESULT_ACTION_QUESTIONS_URL,
  GET_CHECKLIST_ITEMS_URL,
} from "@/utils/constants/constants";

export default {
  methods: {
    async initializeToken() {
      this.token = sessionStorage.getItem("token") || localStorage.getItem("token");
      await this.fetch_csrf_token();
    },

    fetch_csrf_token() {
      return axios
        .get(GET_CSRF_TOKEN, {
          headers: { "Content-Type": "application/json" },
        })
        .then(response => {
          const data = response?.data || {};
          this.csrfToken =
            data.csrf_token ||
            data.token ||
            data.csrfToken ||
            this.get_csrf_from_cookie() ||
            "";
        })
        .catch(() => {
          this.csrfToken = this.get_csrf_from_cookie() || "";
        });
    },

    get_csrf_from_cookie() {
      const name = "csrftoken";
      if (!document.cookie) return null;
      return document.cookie.split(";").reduce((result, cookie) => {
        const trimmedCookie = cookie.trim();
        if (trimmedCookie.startsWith(name + "=")) {
          return decodeURIComponent(trimmedCookie.substring(name.length + 1));
        }
        return result;
      }, null);
    },

    fetch_all_dropdowns() {
      return this.fetch_compliance_dropdowns()
        .then(() => {
          if (this.current_form_id) {
            return this.fetch_compliance_fields();
          }
          return Promise.resolve();
        })
        .then(() => {
          return this.fetch_result_action_questions();
        })
        .then(() => {
          return this.fetch_checklist_items();
        })
        .catch(() => {
          this.show_error("Failed to load some dropdown data");
        });
    },

    fetch_compliance_dropdowns() {
      return axios
        .get(GET_COMPLIANCE_DROPDOWNS_URL, {
          headers: this.computed_headers,
        })
        .then(response => {
          if (response.data.status === "success") {
            this.condition_type_options = (response.data.data.condition_types || []).map(ct => ({
              value: ct.id,
              label: this.format_label(ct.name),
            }));

            this.operator_options = (response.data.data.operators || []).map(op => ({
              value: op.id,
              label: this.format_label(op.operator),
            }));
          } else {
            this.show_error(response.data.message || "Failed to load dropdown data");
          }
        })
        .catch(error => {
          this.show_error("Could not load dropdown options: " + error.message);
        });
    },

    fetch_compliance_fields() {
      return axios
        .get(`${GET_COMPLIANCE_FIELDS_URL}?form_id=${this.current_form_id}`, {
          headers: this.computed_headers,
        })
        .then(response => {
          if (response.data.status === "success") {
            this.compliance_fields = response.data.data || response.data.fields || [];
          }
        })
        .catch(() => {
          this.compliance_fields = [];
        });
    },

    fetch_result_action_questions() {
      return axios
        .get(GET_RESULT_ACTION_QUESTIONS_URL, {
          headers: this.computed_headers,
        })
        .then(response => {
          if (response.data.status === "success") {
            this.result_action_questions = response.data.data || response.data.questions || [];
          }
        })
        .catch(() => {
          this.result_action_questions = [];
        });
    },

    fetch_checklist_items() {
      return axios
        .get(GET_CHECKLIST_ITEMS_URL, {
          headers: this.computed_headers,
        })
        .then(response => {
          if (response.data.status === "success") {
            this.checklist_items = response.data.data || response.data.items || [];
          }
        })
        .catch(() => {
          this.checklist_items = [];
        });
    },

    get_default_operators() {
      const conditionType = this.get_condition_type_string(this.condition_form.condition_type);
      const operatorSets = {
        question_response: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "3", label: "Greater Than" },
          { value: "4", label: "Less Than" },
          { value: "5", label: "Greater Or Equal" },
          { value: "6", label: "Less Or Equal" },
          { value: "8", label: "Contains" },
          { value: "9", label: "Not Contains" },
          { value: "10", label: "Starts With" },
          { value: "11", label: "Ends With" },
          { value: "12", label: "In" },
          { value: "13", label: "Not In" },
        ],
        time_based: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "3", label: "Greater Than" },
          { value: "4", label: "Less Than" },
          { value: "5", label: "Greater Or Equal" },
          { value: "6", label: "Less Or Equal" },
          { value: "7", label: "Between" },
        ],
        user_role: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "8", label: "Contains" },
          { value: "9", label: "Not Contains" },
          { value: "12", label: "In" },
          { value: "13", label: "Not In" },
        ],
        checklist_item: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
        ],
        status_based: [
          { value: "1", label: "Equals" },
          { value: "2", label: "Not Equals" },
          { value: "12", label: "In" },
          { value: "13", label: "Not In" },
        ],
      };
      return Reflect.get(operatorSets, conditionType) || [];
    },

    get_condition_type_string(typeId) {
      if (typeof typeId === "string" && !/^\d+$/.test(typeId)) return typeId;
      return this.get_condition_type_label(typeId);
    },

    get_operator_string(operatorId) {
      if (typeof operatorId === "string" && !/^\d+$/.test(operatorId)) return operatorId;
      const label = this.get_operator_label(operatorId);
      return label ? label.toLowerCase().replace(/[^a-z0-9]/g, "_") : operatorId;
    },

    get_condition_type_class(type) {
      const typeId = this.get_condition_type_string(type);
      const classMap = {
        question_response: "bg-primary",
        time_based: "bg-warning text-dark",
        user_role: "bg-info",
        checklist_item: "bg-purple",
        status_based: "bg-success",
      };
      return Reflect.get(classMap, typeId) || "bg-secondary";
    },

    get_logical_operator_class(operator) {
      const classMap = {
        AND: "bg-success",
        OR: "bg-warning text-dark",
        NOT: "bg-danger",
        XOR: "bg-info",
        NAND: "bg-purple",
      };
      return Reflect.get(classMap, operator) || "bg-secondary";
    },

    format_condition_type(type) {
      const label = this.get_condition_type_label(type);
      if (label) {
        return label
          .split("_")
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }
      return String(type);
    },

    format_operator(operator) {
      return this.get_operator_label(operator) || String(operator);
    },

    format_date(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    format_selected_options(options) {
      if (!options || !Array.isArray(options) || options.length === 0) {
        return null;
      }

      return options
        .map(opt => {
          if (typeof opt === "object" && opt !== null) {
            return opt.label || opt.value || "Unknown";
          }
          return opt;
        })
        .join(", ");
    },

    get_field_item_display(condition) {
      if (condition.field_assignments && condition.field_assignments.length > 0) {
        return condition.field_assignments
          .map(assign => assign.question_text || `Question ${assign.compliance_field}`)
          .join(" + ");
      }

      if (condition.compliance_field) {
        const field = this.compliance_fields.find(f => f.id == condition.compliance_field);
        return field ? field.name || field.field_name || `Field ${field.id}` : "";
      }

      return "";
    },

    format_label(text) {
      if (!text) return "";
      return text
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },

    get_condition_type_label(typeId) {
      const type = this.condition_type_options.find(t => t.value == typeId);
      return type ? type.label : typeId;
    },

    get_operator_label(operatorId) {
      const operator = this.operator_options.find(o => o.value == operatorId);
      return operator ? operator.label : operatorId;
    },

    initialize_modals() {
      const modalConfigs = [
        { ref: "conditionModal", prop: "conditionModal" },
        { ref: "deleteConditionModal", prop: "deleteConditionModal" },
      ];
      modalConfigs.forEach(({ ref, prop }) => {
        const el = Reflect.get(this.$refs, ref);
        if (el && window.bootstrap) {
          Reflect.set(this, prop, new window.bootstrap.Modal(el, { backdrop: true, keyboard: true }));
        }
      });
    },

    open_add_condition_modal() {
      this.reset_condition_form();
      if (this.conditionModal) this.conditionModal.show();
    },

    open_edit_condition_modal(condition) {
      this.condition_form = {
        id: condition.id,
        rule_id: condition.rule_id,
        condition_type: condition.condition_type,
        operator: condition.operator,
        value: condition.value || "",
        comparison_value: condition.comparison_value || "",
        logical_operator: condition.logical_operator || "AND",
        selected_options: condition.selected_options || [],
        compliance_field: condition.compliance_field || null,
        result_action_question: condition.result_action_question || null,
        checklist_item_assignment: condition.checklist_item_assignment || null,
        version: condition.version || 1,
      };
      if (this.conditionModal) this.conditionModal.show();
    },

    close_condition_modal() {
      if (this.conditionModal) this.conditionModal.hide();
      this.reset_condition_form();
    },

    close_delete_modal() {
      if (this.deleteConditionModal) this.deleteConditionModal.hide();
      this.condition_to_delete = null;
    },

    on_condition_type_change() {
      this.condition_form.operator = "";
      this.condition_form.value = "";
      this.condition_form.comparison_value = "";
      this.condition_form.selected_options = [];
      this.condition_form.compliance_field = null;
      this.condition_form.result_action_question = null;
      this.condition_form.checklist_item_assignment = null;
      this.errors = {};
    },

    on_operator_change() {
      this.errors = {};
    },

    get_value_placeholder() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      const operatorStr = this.get_operator_string(this.condition_form.operator);

      const placeholders = {
        question_response: "Enter expected answer",
        time_based: "YYYY-MM-DD or number of days",
        user_role: "Enter role name",
        checklist_item: "Enter status (e.g., completed, pending)",
        status_based: "Enter status value",
      };

      if (["in", "not_in"].includes(operatorStr)) {
        return "Comma-separated values";
      }

      return Reflect.get(placeholders, conditionTypeStr) || "Enter value";
    },

    get_value_max_placeholder() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);

      if (conditionTypeStr === "time_based") {
        return "YYYY-MM-DD (end date)";
      }

      return "Enter end value";
    },

    get_value_hint() {
      const conditionTypeStr = this.get_condition_type_string(this.condition_form.condition_type);
      const operatorStr = this.get_operator_string(this.condition_form.operator);

      if (conditionTypeStr === "time_based") {
        if (["greater_than", "less_than", "greater_or_equal", "less_or_equal"].includes(operatorStr)) {
          return "Enter date in YYYY-MM-DD format";
        }
        return "For dates: YYYY-MM-DD. For days: Enter number";
      }

      if (["in", "not_in"].includes(operatorStr)) {
        return "Separate multiple values with commas (value1, value2, value3)";
      }

      return "Enter the required value";
    },

    go_back() {
      this.$router.push({
        name: "ComplianceRules",
        query: { form_id: this.current_form_id, form_name: this.current_form_name },
      });
    },
  },
};
